import React from 'react';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import FormHelperText from '@mui/joy/FormHelperText';
import { Box } from '@mui/joy';
import { UserpilotHook } from './LabelUserpilotHook';

interface Props {
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: any;
  mdCol?: string;
  label?: string;
  name?: string;
  values?: any;
  onChange?: (e: any) => void;
  isValid?: boolean;
  feedback?: any;
  hiddenFeedback?: boolean;
  placeholder?: string;
  icon: any;
  iconClass?: string;
  type?: string;
  className?: string;
  logo?: string;
  formClass?: string;
  otherProps?: any;
  isMissing?: any;
  labelClass?: string;
  labelCustom?: any;
  onBlur?: any;
  /** When true, label is not rendered (use an external label for layout, e.g. input + button row). */
  hideLabel?: boolean;
  /** Stable Userpilot spotlight hook. */
  dataUp?: string;
}

const TextIconInput = (props: Props) => {
  const showLabel = !props.hideLabel && (Boolean(props.label) || props.labelCustom !== undefined);
  return (
    <FormControl
      id={props.id || props.name}
      disabled={props.disabled}
      error={!!props.isMissing}
      className={props.className}
      data-up={showLabel ? undefined : props.dataUp}
    >
      {!props.hideLabel &&
        (props.labelCustom !== undefined ? (
          <label htmlFor={props.id || props.name} className={props.labelClass}>
            {props.label}
            {props.labelCustom && props.labelCustom}
            <UserpilotHook dataUp={props.dataUp} />
          </label>
        ) : (
          <div
            style={{
              marginBottom: '2px',
            }}
          >
            <FormLabel
              className={`select-label label-14px ${props.labelClass}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                fontSize: '14px',
                marginBottom: '1px !important',
                backgroundColor: props.isMissing ? '#FFEBEB' : '',
              }}
            >
              <span>{props.label}</span>
              {props.logo && (
                <FormLabel className={`csu-logo-select-input-float-right ${props.logo ?? ''}`.trim()} />
              )}
              <UserpilotHook dataUp={props.dataUp} />
            </FormLabel>
          </div>
        ))}

      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            color: 'text.secondary',
          }}
          className={props.iconClass ?? 'text-field-light'}
        >
          {props.icon}
        </Box>
        <Input
          id={props.id || props.name}
          className={props.formClass}
          placeholder={props.placeholder}
          disabled={props.disabled}
          readOnly={props.readOnly}
          onBlur={props.onBlur}
          sx={{
            fontSize: '15px',
            backgroundColor: '#fff !important',
            paddingLeft: '36px',
            // Comprehensive override for all states
            '& .MuiInput-input': {
              backgroundColor: '#fff !important',
            },
            '& .MuiInput-root': {
              backgroundColor: '#fff !important',
            },
            '&:hover': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                backgroundColor: '#fff !important',
              },
            },
            '&.Mui-focused': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                backgroundColor: '#fff !important',
              },
            },
            '&.Mui-focused:hover': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                backgroundColor: '#fff !important',
              },
            },
            // Override for autocomplete/autofill
            '& .MuiInput-input:-webkit-autofill': {
              backgroundColor: '#fff !important',
              '-webkit-box-shadow': '0 0 0 1000px #fff inset !important',
            },
            '& .MuiInput-input:-webkit-autofill:hover': {
              backgroundColor: '#fff !important',
              '-webkit-box-shadow': '0 0 0 1000px #fff inset !important',
            },
            '& .MuiInput-input:-webkit-autofill:focus': {
              backgroundColor: '#fff !important',
              '-webkit-box-shadow': '0 0 0 1000px #fff inset !important',
            },
            // Error styling for MUI Joy Input
            '&.Mui-error': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                color: '#d32f2f',
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                borderColor: '#d32f2f',
                backgroundColor: '#fff !important',
              },
              '&:hover .MuiInput-root': {
                borderColor: '#d32f2f',
                backgroundColor: '#fff !important',
              },
              '&.Mui-focused .MuiInput-root': {
                borderColor: '#d32f2f',
                backgroundColor: '#fff !important',
              },
            },
            // Custom error class styling
            '&.text-danger': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                color: '#d32f2f !important',
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                borderColor: '#d32f2f !important',
                backgroundColor: '#fff !important',
              },
              '&:hover .MuiInput-root': {
                borderColor: '#d32f2f !important',
                backgroundColor: '#fff !important',
              },
              '&.Mui-focused .MuiInput-root': {
                borderColor: '#d32f2f !important',
                backgroundColor: '#fff !important',
              },
            },
            // Success styling
            '&.Mui-colorSuccess': {
              backgroundColor: '#fff !important',
              '& .MuiInput-input': {
                color: '#2e7d32',
                backgroundColor: '#fff !important',
              },
              '& .MuiInput-root': {
                borderColor: '#2e7d32',
                backgroundColor: '#fff !important',
              },
            },
          }}
          type={props.type ?? 'text'}
          defaultValue={props.defaultValue}
          name={props.name}
          value={props.values ?? ''}
          onChange={props.onChange}
          color={props.isValid ? 'success' : undefined}
          error={!!props.isMissing}
          {...props.otherProps}
        />
      </Box>

      {(props.hiddenFeedback === undefined || props.hiddenFeedback === false) && props.feedback && (
        <FormHelperText
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            minHeight: '1rem',
          }}
        >
          {props.feedback}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default TextIconInput;
